import React, { useState, useEffect } from 'react';
import apiClient from '../services/api';

const GrammarKata = () => {
    const [challenge, setChallenge] = useState(null);
    const [userAnswer, setUserAnswer] = useState('');
    const [feedback, setFeedback] = useState(null);
    const [loading, setLoading] = useState(false);
    const [streak, setStreak] = useState(0);

    const fetchChallenge = async () => {
        setLoading(true);
        setFeedback(null);
        setUserAnswer('');
        try {
            const res = await apiClient.get('/learning/grammar-kata');
            setChallenge(res.data);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchChallenge();
    }, []);

    const handleCheck = async () => {
        if (!userAnswer) return;
        setLoading(true);
        try {
            const res = await apiClient.post('/learning/grammar-check', {
                original_sentence: challenge.sentence,
                user_answer: userAnswer
            });
            setFeedback(res.data);
            if (res.data.is_correct) {
                setStreak(s => s + 1);
                // Trigger engagement with UID
                import('../firebase').then(({ auth }) => {
                    const uid = auth.currentUser ? auth.currentUser.uid : 'test_user';
                    apiClient.post('/learning/engagement', {
                        action: 'completed_quiz',
                        user_id: uid
                    });
                });
            } else {
                setStreak(0);
            }
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    if (!challenge) return <div style={styles.card}>Loading Kata...</div>;

    return (
        <div style={styles.card}>
            <div style={styles.header}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ fontSize: '24px' }}>🥋</span>
                    <h3 style={{ margin: 0, color: '#2d2e40' }}>GrammarKata</h3>
                </div>
                <div style={styles.streak}>
                    Count: {streak} 🔥
                </div>
            </div>

            <div style={styles.content}>
                <p style={styles.instruction}>Fix the error in this sentence:</p>

                <div style={styles.problemBox}>
                    "{challenge.sentence}"
                </div>

                {feedback ? (
                    <div style={{
                        ...styles.feedbackBox,
                        background: feedback.is_correct ? '#def7ec' : '#fde8e8',
                        color: feedback.is_correct ? '#03543f' : '#9b1c1c'
                    }}>
                        <strong>{feedback.is_correct ? "Correct!" : "Try Again"}</strong>
                        <p style={{ margin: '5px 0 0' }}>{feedback.feedback}</p>

                        <button onClick={fetchChallenge} style={styles.nextBtn}>
                            Next Challenge &rarr;
                        </button>
                    </div>
                ) : (
                    <div style={styles.inputArea}>
                        <input
                            type="text"
                            value={userAnswer}
                            onChange={(e) => setUserAnswer(e.target.value)}
                            placeholder="Type the correct sentence here..."
                            style={styles.input}
                            onKeyPress={(e) => e.key === 'Enter' && handleCheck()}
                        />
                        <button
                            onClick={handleCheck}
                            disabled={loading}
                            style={styles.checkBtn}
                        >
                            {loading ? 'Checking...' : 'Check'}
                        </button>
                    </div>
                )}

                {!feedback && <div style={styles.hint}>💡 Hint: {challenge.hint}</div>}
            </div>
        </div>
    );
};

const styles = {
    card: {
        background: 'white',
        borderRadius: '16px',
        padding: '24px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
        border: '1px solid #f0f0f0',
        marginBottom: '24px',
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px',
    },
    streak: {
        background: '#fff1f2',
        color: '#e02424',
        padding: '5px 12px',
        borderRadius: '20px',
        fontWeight: 'bold',
        fontSize: '14px',
        border: '1px solid #ffe4e6',
    },
    instruction: {
        margin: '0 0 10px',
        color: '#6b7280',
        fontSize: '14px',
    },
    problemBox: {
        fontSize: '18px',
        fontWeight: '600',
        color: '#1f2937',
        marginBottom: '20px',
        padding: '15px',
        background: '#f9fafb',
        borderRadius: '8px',
        borderLeft: '4px solid #4f46e5',
    },
    inputArea: {
        display: 'flex',
        gap: '10px',
    },
    input: {
        flex: 1,
        padding: '12px',
        borderRadius: '8px',
        border: '1px solid #d1d5db',
        outline: 'none',
        fontSize: '16px',
    },
    checkBtn: {
        padding: '0 24px',
        background: '#4f46e5',
        color: 'white',
        border: 'none',
        borderRadius: '8px',
        fontWeight: '600',
        cursor: 'pointer',
    },
    hint: {
        marginTop: '15px',
        fontSize: '13px',
        color: '#9ca3af',
        fontStyle: 'italic',
    },
    feedbackBox: {
        padding: '15px',
        borderRadius: '8px',
        animation: 'fadeIn 0.3s ease',
    },
    nextBtn: {
        marginTop: '10px',
        padding: '8px 16px',
        background: 'white',
        border: '1px solid currentColor',
        borderRadius: '6px',
        cursor: 'pointer',
        fontWeight: '600',
        fontSize: '13px',
    }
};

export default GrammarKata;
